import classNames from 'classnames';

import {useCallback, useMemo} from 'react';

import ThreadViewerVirtualized from '../virtualized_thread_viewer';

import LoadingScreen from 'components/loading_screen';

import {useAppDispatch} from 'stores/redux_store';

import {selectPostCard} from 'actions/views/rhs';

import type {Post} from 'mattermost-redux/types/posts';

import {measureThreadOpened} from 'features/telemetry/measureThreadOpened';

import {VirtualizedContext} from '../virtualized_thread_viewer/virtualized_context';

import type {Props} from './types';
import {useMarkThreadRead} from './use_mark_thread_read';
import {useUpdateThreadData} from './use_update_thread_data';
import {useThreadViewerProps} from './use_thread_viewer_props';

import './thread_viewer.scss';

export function ThreadViewer(props: Props) {
    const {rootPostId, useRelativeTimestamp, className} = props;
    const {highlightedPostId, postIds, rootPost, selectedPostFocusedAt, channel} = useThreadViewerProps(props);
    const dispatch = useAppDispatch();

    const hasPostsToLoad = useMemo(
        () => rootPost && postIds.length < rootPost?.reply_count + 1,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rootPost?.id],
    );
    const allPostLoaded = rootPost && postIds.length >= rootPost?.reply_count;

    const onThreadLoaded = useCallback(() => {
        // Если все посты уже загружены при открытии треда, то дожидаемся окончания запроса
        // и выполняем измерение.
        if (!hasPostsToLoad) {
            measureThreadOpened(channel);
        }
    }, [hasPostsToLoad, channel]);

    useMarkThreadRead(rootPostId);
    const {loading} = useUpdateThreadData(rootPostId, onThreadLoaded);

    const handleItemsRendered = useCallback(() => {
        // Выполняем измерение, только если при открытии треда есть незагруженные посты с сервера.
        // Игнорируем промежуточные вызовы, которые возникают при отрисовке постов, которые уже есть в редаксе
        // на момент открытия треда.
        if (hasPostsToLoad && allPostLoaded) {
            measureThreadOpened(channel);
        }
    }, [hasPostsToLoad, channel, allPostLoaded]);

    // Если тред открывается впервые, то необходимо дождаться получения всех постов с бэка,
    // прежде чем их отрисовывать, чтобы виртуальный список мог корректно установить позицию скроллинга.
    // Здесь условие postIds.length < rootPost.reply_count + 1 необходимо для того,
    // чтобы понять открывается ли тред в первый раз или же он уже был подгружен до этого
    // (добавляем 1 так как postIds содержит рутовый пост, знак неравенства меньше т.к. могут быть эфемерные посты).
    const showLoadingScreen = !rootPost?.id || (loading && rootPost.reply_count > 0 && postIds.length < rootPost.reply_count + 1);

    const handleCardClick = useCallback(
        (post: Post) => {
            if (!post) {
                return;
            }

            dispatch(selectPostCard(post));
        },
        [dispatch],
    );

    if (showLoadingScreen) {
        return (
            <LoadingScreen
                style={{
                    display: 'grid',
                    placeContent: 'center',
                    flex: '1',
                }}
            />
        );
    }

    return (
        <div className={classNames('ThreadViewer', className)}>
            <div className='post-right-comments-container'>
                {rootPost?.id ? (
                    <VirtualizedContext.Consumer>
                        {({addScrollListener, removeScrollListener}) => (
                            <ThreadViewerVirtualized
                                key={rootPostId}
                                channelId={rootPost.channel_id}
                                onCardClick={handleCardClick}
                                onCardClickPost={handleCardClick}
                                onItemsRendered={handleItemsRendered}
                                postIds={postIds}
                                selected={rootPost}
                                useRelativeTimestamp={useRelativeTimestamp || false}
                                highlightedPostId={highlightedPostId}
                                selectedPostFocusedAt={selectedPostFocusedAt}
                                addScrollListener={addScrollListener}
                                removeScrollListener={removeScrollListener}
                            />
                        )}
                    </VirtualizedContext.Consumer>

                ) : null}
            </div>
        </div>
    );
}
